import { WIDGET_MESSAGES, type WidgetMessage, type WidgetPlacement } from '@rag/channels/protocol';
import posterUrl from './assets/auri-poster.webp?inline';
import { inviteLinks } from './invite.js';

/**
 * The one tag a client pastes into WordPress:
 *
 *   <script src="https://api.example.org/widget/loader.js"
 *           data-key="wk_…"></script>
 *
 * Everything else is derived. The API base comes from this script's own `src`,
 * because the API is what served it, so there is no second attribute to get
 * wrong.
 *
 * Optional attributes: `data-position` (`centro` (default), `derecha` or
 * `izquierda`), `data-accent`, `data-greeting`, `data-name`, `data-role`,
 * `data-lang` (`es` (default) or `en`, the language the panel opens in).
 *
 * ── An iframe, not a shadow root and not an inline mount ─────────────────
 *
 * WordPress themes style aggressively and unpredictably: `div { box-sizing }`,
 * `button { all: unset }`, `* { font-family }` with `!important`, plugins that
 * restyle every input on the page. An inline mount loses that fight, and a
 * shadow root still inherits font sizing and gets reached by anything the theme
 * sets on the host element. An iframe is the only boundary the page cannot
 * cross, in either direction, so the widget also cannot break the client's site.
 *
 * ── What lives out here ──────────────────────────────────────────────────
 *
 * The launcher is not a bubble. It is the assistant's character, drawn inside
 * the iframe; this file contributes the transparent box around it and four
 * layers that must not live inside the frame because they reach past its edge:
 * the presence halo, the backdrop veil, the arrival spotlight, and the control
 * that moves a centred character aside.
 *
 * Until the frame is ready it also draws her itself: the same still the frame
 * opens with, inlined here, so she is on screen as soon as `<body>` exists
 * rather than after the frame's bundle has arrived. The frame takes over
 * underneath it, and it fades out.
 *
 * The box has a fixed size per state (the character alone when closed, the
 * character plus the docked panel when open, the viewport in mobile
 * fullscreen), and this file sizes it. The frame only says which state it is
 * in.
 *
 * ── What this file must not do ───────────────────────────────────────────
 *
 * It runs on somebody else's page, so it touches nothing it did not create,
 * defines no globals, and adds three elements to `<body>`. Anything it leaves
 * behind is a bug report from a client whose site broke.
 */

const NAMESPACE = 'rag-chat-widget';

/** Character box, corner and centred (`--cra-box-*`). */
const BOX_CORNER = 160;
const BOX_CENTER = 320;
/** Character ↔ panel, and the docked panel itself. */
const GAP = 6;
const PANEL_W = 384;
/**
 * The centred panel, ~30% wider than the docked one (384 × 1.3). Centred is the
 * layout with room to spare, and the transcript is what the room is for. The
 * docked panel keeps 384, and the text column inside is capped in the frame's
 * stylesheet (`.cra-panel-host--centro .log`), so this can grow without the
 * measure becoming unreadable.
 */
const PANEL_W_CENTER = 500;
const PANEL_H = 620;
/** Air the open box keeps from the window edge; below it, fullscreen. */
const FULLSCREEN_AIR = 24;

/*
 * The arrival, in three beats: Auri enters
 * growing, with her halo; a quarter of a second later the rest of the site dims
 * around her; and when the spotlight is already withdrawing, she speaks. All
 * three at once read as a single blink and none of them registers.
 */
const SPOTLIGHT_DELAY_MS = 240;
const SPOTLIGHT_MS = 2400;
const CARD_DELAY_AFTER_SPOTLIGHT_MS = 1800;
const CARD_DELAY_MS = 1000;
/** The card's fade, then its node is removed. */
const CARD_REMOVE_MS = 500;
/** The stand-in's fade once the frame is drawing her, then its node is removed. */
const STAND_IN_REMOVE_MS = 400;
/**
 * No `rag:ready` for this long, counted while the tab is visible: she steps
 * out of sight. Not for good, as a late `rag:ready` brings her back.
 */
const SILENCE_MS = 10_000;
/**
 * The frame waits for the page's `load` (see startFrame), but `load` waits for
 * every image and third-party script, and on a WordPress site one hanging
 * resource means it never fires. After this long she loads regardless.
 */
const FRAME_BACKSTOP_MS = 5000;
/** The placement flight: leaves decisively and brakes on arrival. */
const FLIGHT_MS = 460;
const FLIGHT_EASE = 'cubic-bezier(.22, .78, .28, 1)';

/** Invitation copy. The title carries Auri's name; the text follows it. */
const INVITE_TEXT =
  'Estoy aquí para ayudarte a encontrar el curso ideal para ti y a inscribirte en unos minutos. ' +
  'Y si quieres apoyar nuestra labor, también te ayudo a hacer un donativo. ' +
  '¿Qué te gustaría aprender?';

const PLACEMENTS: readonly WidgetPlacement[] = ['centro', 'derecha', 'izquierda'];

/** sessionStorage keys: the visit, not the visitor. */
const KEY_SPOTLIGHT = 'cra:foco';
const KEY_INVITE = 'cra:invita';
const KEY_MOVED = 'cra:apartada';

interface WidgetConfig {
  apiBase: string;
  key: string;
  title: string;
  greeting: string;
  accent: string;
  placement: WidgetPlacement;
  name: string;
  role: string;
  /**
   * Which language the panel opens in, before the visitor touches the switch
   * in its header. `data-lang` when the tag sets it, otherwise the host page's
   * own `<html lang>` (an English page is a fair guess that its readers want
   * English); the switch overrides either for good.
   */
  lang: string;
}

/**
 * Session-scoped memory, for a visitor walking between pages. Never required:
 * in private browsing, or with cookies blocked, merely reading it throws, and if
 * that blew up here the assistant would never be mounted.
 */
const store = {
  get(key: string): string | null {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      // Private mode or blocked storage: the widget still works on this page.
    }
  },
  remove(key: string): void {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // As above.
    }
  },
};

function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

const HEX_COLOUR = /^#[0-9a-f]{3,8}$/i;

/**
 * `data-accent` first, then the host page's own `--accent` (a theme that
 * already defines one is picked up), then the brand red. Hex only, because it
 * is also handed to the frame, which accepts nothing else.
 */
function resolveAccent(attribute: string | undefined): string {
  const fromTag = attribute?.trim();
  if (fromTag && HEX_COLOUR.test(fromTag)) return fromTag;

  const fromPage = getComputedStyle(document.documentElement).getPropertyValue('--accent').trim();
  if (HEX_COLOUR.test(fromPage)) return fromPage;

  return '#c8102e';
}

/**
 * `document.currentScript` is only correct while the script is executing, so it
 * is read immediately rather than inside a later callback.
 */
function readConfig(): WidgetConfig | null {
  const script = (document.currentScript ??
    document.querySelector<HTMLScriptElement>(
      `script[data-key][src*="loader"]`,
    )) as HTMLScriptElement | null;

  if (!script) {
    console.error('[rag-widget] could not locate its own <script> tag');
    return null;
  }

  const key = script.dataset.key?.trim();
  if (!key) {
    console.error('[rag-widget] missing data-key on the script tag');
    return null;
  }

  // The API that served this file. `data-api` overrides it for local work,
  // where the loader may be served by a static server instead.
  let apiBase = script.dataset.api?.trim() ?? '';
  if (!apiBase) {
    try {
      apiBase = new URL(script.src, window.location.href).origin;
    } catch {
      console.error('[rag-widget] could not derive the API base from the script src');
      return null;
    }
  }

  // A visitor who moved the character aside keeps it there for the visit: it is
  // re-read at mount, so a WordPress page load does not undo the gesture.
  const requested = (script.dataset.position ?? '').trim();
  const placement: WidgetPlacement =
    store.get(KEY_MOVED) === '1'
      ? 'derecha'
      : (PLACEMENTS as readonly string[]).includes(requested)
        ? (requested as WidgetPlacement)
        : 'centro';

  return {
    apiBase: apiBase.replace(/\/$/, ''),
    key,
    title: script.dataset.title?.trim() || 'Asistente Cruz Roja',
    greeting: script.dataset.greeting?.trim() || '',
    accent: resolveAccent(script.dataset.accent),
    placement,
    name: script.dataset.name?.trim() || '',
    role: script.dataset.role?.trim() || '',
    lang: (script.dataset.lang || document.documentElement.lang || '').trim().toLowerCase(),
  };
}

/**
 * The host-page styles.
 *
 * The tokens are declared on the widget's own three elements rather than on
 * `:root`, so nothing is added to the client's global variable namespace.
 * Every rule is namespaced under `.cra`; no bare element selectors.
 */
const HOST_CSS = `
.cra, .cra-fondo, .cra-foco {
  --cra-ink: #0d1220;
  --cra-ink-soft: #8b93a5;
  --cra-surface: rgba(255, 255, 255, .94);
  --cra-line: #e7e9f0;
  --cra-line-strong: #d7dbe6;
  --cra-z-launcher: 99999;
  --cra-z-overlay: 99998;
  --cra-box-corner: ${BOX_CORNER}px;
  --cra-box-center: ${BOX_CENTER}px;
  --cra-anchor-bottom: -10px;
  --cra-anchor-side: 0px;
  --cra-dismiss-size: 26px;
  --cra-dismiss-top: 40px;
  --cra-dismiss-right: 16px;
  --cra-dismiss-radius: 50%;
  --cra-halo-inset: -34%;
  --cra-veil-tint: rgba(13, 18, 32, .12);
  --cra-veil-blur: 5px;
  --cra-spot-0: rgba(9, 13, 24, 0);
  --cra-spot-1: rgba(9, 13, 24, .42);
  --cra-spot-2: rgba(9, 13, 24, .5);
  --cra-spot-clear: 150px;
  --cra-spot-ramp: 380px;
  --cra-shadow-dismiss: 0 4px 12px -6px rgba(13, 18, 32, .45);
  --cra-t-fade: .4s ease;
  --cra-t-veil: .35s ease;
  --cra-t-spot: .6s ease;
  --cra-t-halo: .6s ease;
  --cra-t-control: .22s ease;
  --cra-t-control-fast: .18s ease;
  --cra-enter-dur: .66s;
  --cra-enter-ease: cubic-bezier(.22, .95, .3, 1);
  --cra-enter-from: .82;
  --cra-pulse-dur: 5.2s;
  --cra-pulse-delay: .7s;
  --cra-font: "Segoe UI", "Helvetica Neue", Inter, system-ui, -apple-system, sans-serif;
  --cra-font-size: 12px;
  --cra-line-height: 1;
}

.cra { --cra-halo-inner: rgba(200, 16, 46, .19); --cra-halo-mid: rgba(200, 16, 46, .07); }
@supports (color: color-mix(in srgb, red 50%, transparent)) {
  .cra {
    --cra-halo-inner: color-mix(in srgb, var(--cra-accent) 19%, transparent);
    --cra-halo-mid: color-mix(in srgb, var(--cra-accent) 7%, transparent);
  }
}

.cra, .cra *, .cra *::before, .cra *::after, .cra-fondo, .cra-foco { box-sizing: border-box; }

.cra {
  position: fixed;
  right: var(--cra-anchor-side);
  bottom: var(--cra-anchor-bottom);
  left: auto;
  top: auto;
  z-index: var(--cra-z-launcher);
  width: var(--cra-box-corner);
  height: var(--cra-box-corner);
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  font-family: var(--cra-font);
  font-size: var(--cra-font-size);
  font-weight: 400;
  font-style: normal;
  line-height: var(--cra-line-height);
  letter-spacing: normal;
  text-align: left;
  text-transform: none;
  text-indent: 0;
  color: var(--cra-ink);
  -webkit-text-size-adjust: 100%;
  opacity: 0;
  transition: opacity var(--cra-t-fade);
}
.cra--visible { opacity: 1; }
.cra--callada { visibility: hidden !important; pointer-events: none !important; }
.cra--izq { right: auto; left: var(--cra-anchor-side); }
.cra--centro {
  right: auto;
  bottom: auto;
  left: 50%;
  top: 50%;
  width: var(--cra-box-center);
  height: var(--cra-box-center);
  transform: translate(-50%, -50%);
}

.cra__frame {
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  line-height: var(--cra-line-height);
  color-scheme: normal;
  /* Transparent rather than hidden until the frame says it is ready: a hidden
     iframe gets its rendering throttled, and it is booting in there. */
  opacity: 0;
  pointer-events: none;
}
.cra--listo .cra__frame { opacity: 1; pointer-events: auto; }
.cra--visible .cra-espera { animation: cra-entra var(--cra-enter-dur) var(--cra-enter-ease) both; }
@keyframes cra-entra { from { transform: scale(var(--cra-enter-from)); } to { transform: none; } }

/* --- the stand-in -------------------------------------------------------
   Auri before the frame is ready: the frame's own opening still, placed and
   sized as the frame's stage places it (frame.html, .cra-stage and .fb-bot),
   so the hand-over underneath it changes nothing. It holds the first beat of
   the frame's bob (translate 0 -10px), which is where the frame's still is
   when it appears. A real <button> on the host page, hence the !important. */
.cra-espera {
  box-sizing: border-box !important;
  display: block !important;
  position: absolute !important;
  z-index: 2 !important;
  right: 0 !important;
  bottom: 0 !important;
  left: auto !important;
  top: auto !important;
  width: var(--cra-box-corner) !important;
  height: var(--cra-box-corner) !important;
  margin: 0 !important;
  padding: 0 !important;
  font: inherit !important;
  color: inherit !important;
  background: transparent !important;
  border: 0 !important;
  border-radius: 50% !important;
  box-shadow: none !important;
  -webkit-appearance: none !important;
  appearance: none !important;
  cursor: pointer !important;
  transition: opacity .35s ease !important;
}
.cra--izq .cra-espera { right: auto !important; left: 0 !important; }
.cra--centro .cra-espera {
  right: auto !important;
  bottom: auto !important;
  left: 0 !important;
  top: calc(50% - var(--cra-box-center) / 2) !important;
  width: var(--cra-box-center) !important;
  height: var(--cra-box-center) !important;
}
.cra-espera:focus-visible { outline: 2px solid var(--cra-accent) !important; outline-offset: 2px !important; }
.cra-espera--fuera { opacity: 0 !important; pointer-events: none !important; }
.cra-espera__img {
  display: block;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  object-fit: contain;
  translate: 0 -10px;
  pointer-events: none;
}
/* In a corner the frame lifts the still by 2.8% of the box (CORNER_LIFT). */
.cra:not(.cra--centro) .cra-espera__img { transform: translateY(-2.8%); }

/* --- the loading panel --------------------------------------------------
   A press before the frame is ready: the panel's place, in the panel's skin
   (frame.html, --crp-panel-*), until the real one opens over it. Placed by
   layout(), which knows where the character's band is. */
.cra-cargando {
  position: absolute;
  z-index: 2;
  top: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 0;
  padding: 0;
  font-family: var(--cra-font) !important;
  font-size: 13px;
  line-height: 1.4;
  color: #6d665a;
  background: #fbfaf7;
  border: 1px solid #e6e2d9;
  border-radius: 20px;
  box-shadow: 0 26px 56px -30px rgba(60, 50, 35, .45);
  opacity: 0;
  transition: opacity .25s ease;
}
.cra-cargando--visible { opacity: 1; }
.cra--pantalla .cra-cargando { border-radius: 0; border: 0; }
.cra-cargando__giro {
  display: block;
  width: 26px;
  height: 26px;
  margin: 0;
  padding: 0;
  border: 3px solid #e6e2d9;
  border-top-color: var(--cra-accent);
  border-radius: 50%;
  animation: cra-giro .8s linear infinite;
}
@keyframes cra-giro { to { transform: rotate(360deg); } }

.cra-halo {
  position: absolute;
  inset: var(--cra-halo-inset);
  z-index: 0;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 50%;
  pointer-events: none;
  background: radial-gradient(circle, var(--cra-halo-inner), var(--cra-halo-mid) 46%, transparent 70%);
  opacity: 0;
  transform: scale(.82);
  transition: opacity var(--cra-t-halo), transform var(--cra-t-halo);
}
.cra--visible .cra-halo {
  opacity: 1;
  transform: none;
  animation: cra-latido var(--cra-pulse-dur) ease-in-out var(--cra-pulse-delay) infinite;
}
@keyframes cra-latido {
  0%, 100% { transform: scale(.95); opacity: .72; }
  50% { transform: scale(1.05); opacity: 1; }
}
.cra--abierto .cra-halo, .cra--pantalla .cra-halo { opacity: 0; animation: none; }

.cra-fondo {
  position: fixed;
  inset: 0;
  z-index: var(--cra-z-overlay);
  margin: 0;
  padding: 0;
  border: 0;
  pointer-events: none;
  background: var(--cra-veil-tint);
  -webkit-backdrop-filter: blur(var(--cra-veil-blur));
  backdrop-filter: blur(var(--cra-veil-blur));
  opacity: 0;
  transition: opacity var(--cra-t-veil);
}
.cra-fondo--visible { opacity: 1; }

.cra-foco {
  position: fixed;
  inset: 0;
  z-index: var(--cra-z-overlay);
  margin: 0;
  padding: 0;
  border: 0;
  pointer-events: none;
  background: radial-gradient(circle at var(--cra-foco-x, 50%) var(--cra-foco-y, 50%),
    var(--cra-spot-0) 0,
    var(--cra-spot-0) var(--cra-spot-clear),
    var(--cra-spot-1) var(--cra-spot-ramp),
    var(--cra-spot-2) 100%);
  opacity: 0;
  transition: opacity var(--cra-t-spot);
}
.cra-foco--visible { opacity: 1; }

/* The one real <button> on the host page, so the one thing a theme's
   "button { all: unset !important }" reaches. Hence !important throughout,
   state rules included, so they still outrank each other by specificity. */
.cra-apartar {
  box-sizing: border-box !important;
  display: none !important;
  position: absolute !important;
  z-index: 3 !important;
  top: var(--cra-dismiss-top) !important;
  right: var(--cra-dismiss-right) !important;
  width: var(--cra-dismiss-size) !important;
  height: var(--cra-dismiss-size) !important;
  place-items: center !important;
  margin: 0 !important;
  padding: 0 !important;
  font-family: var(--cra-font) !important;
  font-size: var(--cra-font-size) !important;
  font-weight: 400 !important;
  line-height: var(--cra-line-height) !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  -webkit-appearance: none !important;
  appearance: none !important;
  color: var(--cra-ink-soft) !important;
  background: var(--cra-surface) !important;
  border: 1px solid var(--cra-line) !important;
  border-radius: var(--cra-dismiss-radius) !important;
  box-shadow: var(--cra-shadow-dismiss) !important;
  cursor: pointer !important;
  opacity: 0 !important;
  pointer-events: none !important;
  transform: scale(.9) !important;
  transition:
    opacity var(--cra-t-control),
    transform var(--cra-t-control),
    color var(--cra-t-control-fast),
    border-color var(--cra-t-control-fast) !important;
}
.cra-apartar > .cra-icon { display: block !important; width: 10px !important; height: 10px !important; }
.cra--centro .cra-apartar { display: grid !important; }
.cra--centro:hover .cra-apartar,
.cra--centro .cra-apartar:focus-visible { opacity: 1 !important; pointer-events: auto !important; transform: none !important; }
.cra-apartar:hover { color: var(--cra-ink) !important; border-color: var(--cra-line-strong) !important; }
.cra-apartar:focus-visible { outline: 2px solid var(--cra-accent) !important; outline-offset: 2px !important; }
.cra--centro.cra--abierto .cra-apartar, .cra--pantalla .cra-apartar { display: none !important; }
@media (hover: none) {
  .cra--centro .cra-apartar { opacity: .9 !important; pointer-events: auto !important; transform: none !important; }
}

.cra.cra--pantalla {
  inset: 0;
  right: 0;
  bottom: 0;
  width: auto !important;
  height: auto !important;
  transform: none;
}

@media (prefers-reduced-motion: reduce) {
  .cra, .cra-fondo, .cra-foco, .cra-apartar, .cra-espera, .cra-cargando { transition-duration: .001ms !important; }
  .cra--visible .cra-halo, .cra--visible .cra-espera { animation: none !important; }
  /* The frame does not bob her either. */
  .cra-espera__img { translate: none; }
  .cra-cargando__giro { animation-duration: 2.4s; }
}

@media print {
  .cra, .cra-fondo, .cra-foco { display: none !important; }
}

/* --- invitation card (WIDGET_CHARACTER_BEHAVIOUR.md §7.3) ---------------
   A character in the middle of the page says neither what it is nor what it
   is for. The invitation is Auri's voice before anyone touches her — who she
   is, what she is for, and three questions that can be pressed without typing.

   It lives out here and not inside the iframe because inside it would need a
   box of nearly 700px with the chat still closed, and that transparent
   rectangle would swallow the clicks of half the middle of the page.

   Its buttons carry !important for the same reason .cra-apartar does: they are
   real <button>s on the host page, the one thing "button { all: unset
   !important }" in a theme reaches. */
.cra-invita {
  position: absolute;
  z-index: 2;
  width: max-content;
  max-width: min(330px, calc(100vw - 32px));
  margin: 0;
  padding: 14px 34px 14px 16px;
  font-family: var(--cra-font) !important;
  font-size: 13px;
  line-height: 1.4;
  text-align: left;
  color: var(--cra-ink);
  background: #fff;
  border: 1px solid var(--cra-line);
  border-radius: 18px;
  box-shadow: 0 20px 46px -22px rgba(13, 18, 32, .5),
              0 3px 12px -6px rgba(13, 18, 32, .16);
  /* Above her in a bottom corner. The 24px subtracted from 100% is part of the
     air the render leaves inside her box at the top; without it the card would
     float clear of her head and the tail would point at nothing. */
  bottom: calc(100% - 24px);
  right: 6px;
  transform-origin: bottom right;
  opacity: 0;
  transform: translateY(10px) scale(.96);
  pointer-events: none;
  transition: opacity .34s ease, transform .44s cubic-bezier(.2, .9, .25, 1);
}
.cra-invita--visible { opacity: 1; transform: none; pointer-events: auto; }
.cra--izq .cra-invita { right: auto; left: 6px; transform-origin: bottom left; }
/* Centred, the card goes below rather than beside: when it opens, the chat
   takes exactly the right-hand flank, and a card there would be the first
   thing covered. */
.cra--centro .cra-invita {
  top: calc(100% - 46px);
  bottom: auto;
  left: 50%;
  right: auto;
  transform-origin: top center;
  transform: translateX(-50%) translateY(-10px) scale(.96);
}
.cra--centro .cra-invita--visible { transform: translateX(-50%); }

/* The tail that ties it to Auri: a rotated square with only its two outward
   sides bordered, so the rest melts into the card. */
.cra-invita::after {
  content: "";
  position: absolute;
  width: 12px;
  height: 12px;
  background: #fff;
  bottom: -7px;
  right: 44px;
  border-right: 1px solid var(--cra-line);
  border-bottom: 1px solid var(--cra-line);
  transform: rotate(45deg);
}
.cra--izq .cra-invita::after { right: auto; left: 44px; }
.cra--centro .cra-invita::after {
  bottom: auto;
  top: -7px;
  left: 50%;
  right: auto;
  margin-left: -6px;
  border: 0;
  border-left: 1px solid var(--cra-line);
  border-top: 1px solid var(--cra-line);
}

/* The text is a button (reachable by Tab, fires on Enter), so its
   button-ness has to be undone. */
.cra-invita__abrir {
  box-sizing: border-box !important;
  display: block !important;
  width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  font: inherit !important;
  color: inherit !important;
  text-align: left !important;
  background: none !important;
  border: 0 !important;
  border-radius: 8px !important;
  cursor: pointer !important;
}
.cra-invita__titulo {
  display: block;
  margin: 0 0 3px;
  padding: 0;
  font-family: var(--cra-font) !important;
  font-size: 14.5px;
  font-weight: 650;
  line-height: 1.3;
  letter-spacing: -.01em;
  color: var(--cra-ink);
}
.cra-invita__texto {
  display: block;
  margin: 0;
  padding: 0;
  font-family: var(--cra-font) !important;
  font-size: 13px;
  line-height: 1.46;
  color: #5b6478;
}
.cra-invita__chips { display: flex; flex-wrap: wrap; gap: 6px; margin: 11px 0 0; padding: 0; border: 0; background: none; }
.cra-invita__chip {
  box-sizing: border-box !important;
  /* A <button> as well as an <a>: theme button styles must not reach it. */
  width: auto !important;
  min-height: 0 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  box-shadow: none !important;
  text-decoration: none !important;
  display: inline-block !important;
  margin: 0 !important;
  padding: 7px 12px !important;
  font-family: inherit !important;
  font-size: 12.2px !important;
  font-weight: 400 !important;
  line-height: 1.2 !important;
  color: var(--cra-ink) !important;
  background: #fff !important;
  border: 1px solid var(--cra-accent) !important;
  border-radius: 999px !important;
  cursor: pointer !important;
  transition: color .18s ease, border-color .18s ease, transform .18s ease !important;
}
.cra-invita__chip:hover {
  color: var(--cra-accent) !important;
  border-color: color-mix(in srgb, var(--cra-accent) 32%, #fff) !important;
  transform: translateY(-1px) !important;
}
.cra-invita__abrir:focus-visible,
.cra-invita__chip:focus-visible,
.cra-invita__cerrar:focus-visible {
  outline: 2px solid var(--cra-accent) !important;
  outline-offset: 2px !important;
}
/* Small and discreet on purpose: it has to be there — nobody should feel
   pursued by a card — but not compete with the shortcuts. Closing it leaves
   Auri; what goes is the text. */
.cra-invita__cerrar {
  box-sizing: border-box !important;
  position: absolute !important;
  top: 7px !important;
  right: 7px !important;
  width: 22px !important;
  height: 22px !important;
  display: grid !important;
  place-items: center !important;
  margin: 0 !important;
  padding: 0 !important;
  font-family: inherit !important;
  font-size: 13px !important;
  line-height: 1 !important;
  color: #9aa2b4 !important;
  background: transparent !important;
  border: 0 !important;
  border-radius: 7px !important;
  cursor: pointer !important;
}
.cra-invita__cerrar:hover { color: var(--cra-ink) !important; background: #f4f5f8 !important; }

/* With the conversation open the card is redundant. */
.cra--abierto .cra-invita, .cra--pantalla .cra-invita { opacity: 0; pointer-events: none; }

/* On narrow screens the shortcuts would stack one per row; a point smaller,
   they fit two at a time. */
@media (max-width: 480px) {
  .cra-invita { max-width: calc(100vw - 28px); padding: 12px 30px 12px 14px; }
  .cra-invita__chip { font-size: 11.6px !important; padding: 6px 10px !important; }
}

@media (prefers-reduced-motion: reduce) {
  .cra-invita { transition-duration: .001ms !important; }
}
`;

function injectStyles(): void {
  if (document.getElementById(`${NAMESPACE}-style`)) return;

  const style = document.createElement('style');
  style.id = `${NAMESPACE}-style`;
  style.textContent = HOST_CSS;
  document.head.appendChild(style);
}

function div(className: string): HTMLDivElement {
  const node = document.createElement('div');
  node.className = className;
  node.setAttribute('aria-hidden', 'true');
  return node;
}

function boot(config: WidgetConfig): void {
  // One widget per page, however many times the tag was pasted.
  if (document.querySelector(`.cra[data-${NAMESPACE}]`)) return;
  injectStyles();

  const reducedMotion = prefersReducedMotion();

  // The veil and the spotlight are siblings of the box, because they cover the
  // viewport and the box is 160–320px.
  const veil = div('cra-fondo');
  const spotlight = div('cra-foco');

  const box = document.createElement('div');
  box.className = 'cra';
  box.setAttribute(`data-${NAMESPACE}`, '');
  box.style.setProperty('--cra-accent', config.accent);

  const halo = div('cra-halo');

  const frame = document.createElement('iframe');
  frame.className = 'cra__frame';
  frame.title = config.title;
  // The frame runs our code from our origin and needs script and same-origin
  // (for the fetch to /chat, which is same-origin from inside it). It is not
  // granted top-navigation: it can never replace the host page.
  //
  // Popups are granted, and allowed to escape the sandbox, for one reason: the
  // enrolment card and the answer's links open the store in a new tab, and a
  // sandboxed tab would break the store's own checkout. Without this every
  // `target="_blank"` link in the panel silently does nothing.
  frame.setAttribute(
    'sandbox',
    'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox',
  );
  frame.setAttribute('allow', 'autoplay; clipboard-write');
  frame.setAttribute('referrerpolicy', 'no-referrer-when-downgrade');
  frame.setAttribute('allowtransparency', 'true');
  frame.style.background = 'transparent';

  // Auri until the frame is ready. Pressing her before then opens the loading
  // panel, and the real one as soon as the frame can show it.
  const standIn = document.createElement('button');
  standIn.type = 'button';
  standIn.className = 'cra-espera';
  standIn.setAttribute('aria-label', 'Abrir conversación con el asistente');
  const still = document.createElement('img');
  still.className = 'cra-espera__img';
  still.src = posterUrl;
  still.alt = '';
  still.draggable = false;
  standIn.appendChild(still);

  // Last in the box, so tab order reaches the assistant before the way to move
  // it aside. The name says *move*: nothing closes, it relocates.
  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.className = 'cra-apartar';
  dismiss.setAttribute('aria-label', 'Apartar el asistente a la esquina');
  dismiss.title = 'Apartar a la esquina';
  dismiss.innerHTML =
    '<svg class="cra-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M5 5 19 19M19 5 5 19" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/>' +
    '</svg>';

  box.append(halo, frame, standIn, dismiss);

  const expectedOrigin = new URL(config.apiBase, window.location.href).origin;

  let placement = config.placement;
  let open = false;
  let ready = false;
  let fullscreen = false;
  /** Pressed before the frame was ready: it opens the panel the moment it is. */
  let openWhenReady = false;
  let loading: HTMLElement | null = null;

  const toFrame = (message: WidgetMessage): void => {
    frame.contentWindow?.postMessage(message, expectedOrigin);
  };

  /** The character's band: 320 centred, 160 in a corner. */
  const side = (): number => (placement === 'centro' ? BOX_CENTER : BOX_CORNER);
  /** Centred and left-hand, she sits in the left band and the panel opens to her right. */
  const characterOnLeft = (): boolean => placement !== 'derecha';

  /**
   * Where Auri is, which is not the centre of the box. She occupies a band
   * against one edge and the panel takes the rest; the head turn and the
   * spotlight both depend on this.
   */
  const characterCentre = (): { x: number; y: number } => {
    // The box rather than the frame, which fills it: the same rectangle, and
    // it is already measurable while the stand-in is the one on screen.
    const r = box.getBoundingClientRect();
    return {
      x: characterOnLeft() ? r.left + side() / 2 : r.right - side() / 2,
      y: placement === 'centro' ? (r.top + r.bottom) / 2 : r.bottom - side() / 2,
    };
  };

  /**
   * Sizes and places the box for the current state. Size and position
   * change in one step, never animated: the frame repaints its scene at every
   * intermediate size and cannot keep up.
   */
  const layout = (): void => {
    const openWidth = side() + GAP + (placement === 'centro' ? PANEL_W_CENTER : PANEL_W);

    // Below this width the chat opens fullscreen. The cut is not a phone
    // measurement chosen by eye: it is what the open box measures plus the air
    // it is left. Centred that is 710 + 24 = 734; in a corner, 550 + 24 = 574.
    //
    // Measured with the docked panel width in both cases, so the wider centred
    // panel leaves the breakpoint where it was. Between 734px and the full
    // centred width the box simply takes what fits (the `Math.min` below).
    const fullscreenCut = side() + GAP + PANEL_W;
    const nextFullscreen = open && window.innerWidth <= fullscreenCut + FULLSCREEN_AIR;

    box.classList.toggle('cra--centro', placement === 'centro');
    box.classList.toggle('cra--izq', placement === 'izquierda');
    box.classList.toggle('cra--abierto', open);
    box.classList.toggle('cra--pantalla', nextFullscreen);
    box.style.width =
      open && !nextFullscreen ? `${Math.min(openWidth, window.innerWidth - FULLSCREEN_AIR)}px` : '';
    box.style.height =
      open && !nextFullscreen ? `${Math.min(PANEL_H, window.innerHeight - FULLSCREEN_AIR)}px` : '';
    // Fullscreen leaves nothing of the site in view: the veil would only cost
    // a blur nobody gets to see.
    veil.classList.toggle('cra-fondo--visible', open && !nextFullscreen);

    // The loading panel takes the panel's side of the box: all of it in
    // fullscreen, otherwise everything past the character's band.
    if (loading) {
      const band = nextFullscreen ? '0px' : `${side() + GAP}px`;
      loading.style.left = characterOnLeft() ? band : '0px';
      loading.style.right = characterOnLeft() ? '0px' : band;
    }

    fullscreen = nextFullscreen;
    if (ready) toFrame({ type: WIDGET_MESSAGES.layout, placement, fullscreen });
  };

  /**
   * Moves the character to another placement with a FLIP flight.
   *
   * Scale goes per axis: returning from the corner with the conversation open
   * the box goes from 550×620 to 710×620, width only. The resting transform is
   * read from the computed style rather than assumed to be `none` (centred, it
   * is the centring translate). `will-change` is set just before and cleared on
   * landing: permanent, it would hold a texture the size of the open box for the
   * whole visit.
   *
   * The scene inside is deliberately not paused during the flight: the canvas
   * empties when resized, and a stopped loop would leave her invisible for it.
   */
  const flyTo = (next: WidgetPlacement): void => {
    if (next === placement) return;
    const first = box.getBoundingClientRect();
    placement = next;
    layout();
    if (reducedMotion) return;

    const last = box.getBoundingClientRect();
    if (!last.width || !last.height) return;

    const resting = getComputedStyle(box).transform;
    const base = resting && resting !== 'none' ? resting : '';
    const dx = first.left - last.left;
    const dy = first.top - last.top;
    const sx = first.width / last.width;
    const sy = first.height / last.height;

    box.style.transformOrigin = '0 0';
    box.style.willChange = 'transform';
    box.style.transition = 'none';
    box.style.transform = `translate(${dx}px, ${dy}px) ${base} scale(${sx}, ${sy})`;
    void box.offsetWidth; // commit the inverted frame before animating out of it

    box.style.transition = `transform ${FLIGHT_MS}ms ${FLIGHT_EASE}, opacity var(--cra-t-fade)`;
    box.style.transform = `translate(0px, 0px) ${base} scale(1, 1)`;

    let landed = false;
    const land = (): void => {
      if (landed) return;
      landed = true;
      box.removeEventListener('transitionend', onEnd);
      box.style.transition = '';
      box.style.transform = '';
      box.style.transformOrigin = '';
      box.style.willChange = '';
    };
    const onEnd = (event: TransitionEvent): void => {
      if (event.target === box && event.propertyName === 'transform') land();
    };
    box.addEventListener('transitionend', onEnd);
    window.setTimeout(land, FLIGHT_MS + 80);
  };

  // ── arrival spotlight ─────────────────────────────────────────

  const GESTURES = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;
  let spotlightOn = false;
  const clearSpotlight = (): void => {
    if (!spotlightOn) return;
    spotlightOn = false;
    spotlight.classList.remove('cra-foco--visible');
    GESTURES.forEach((name) => document.removeEventListener(name, clearSpotlight, true));
  };

  /** Once per visit, and never with reduced motion. Returns whether it ran. */
  const spotlightOnce = (): boolean => {
    if (reducedMotion || store.get(KEY_SPOTLIGHT)) return false;
    store.set(KEY_SPOTLIGHT, '1');

    window.setTimeout(() => {
      if (open) return;
      const c = characterCentre();
      spotlight.style.setProperty('--cra-foco-x', `${Math.round(c.x)}px`);
      spotlight.style.setProperty('--cra-foco-y', `${Math.round(c.y)}px`);
      spotlight.classList.add('cra-foco--visible');
      spotlightOn = true;
      // The first gesture clears it: the visitor has seen her and is doing
      // something else.
      GESTURES.forEach((name) =>
        document.addEventListener(name, clearSpotlight, { capture: true, passive: true }),
      );
      window.setTimeout(clearSpotlight, SPOTLIGHT_MS);
    }, SPOTLIGHT_DELAY_MS);
    return true;
  };

  // ── invitation card ──────────────────────────────────────

  let card: HTMLElement | null = null;

  const hideCard = (): void => {
    const node = card;
    if (!node) return;
    card = null;
    node.classList.remove('cra-invita--visible');
    window.setTimeout(() => node.remove(), CARD_REMOVE_MS);
  };

  /**
   * Once per visit: sessionStorage, not localStorage, because someone coming
   * back another day is someone to welcome again. Every string goes in with
   * textContent, never as markup.
   */
  const showCard = (): void => {
    if (open || card || store.get(KEY_INVITE)) return;
    store.set(KEY_INVITE, '1');

    const aside = document.createElement('aside');
    aside.className = 'cra-invita';
    aside.setAttribute('aria-label', `${config.name || 'Auri'} · ${config.title}`);

    const opener = document.createElement('button');
    opener.type = 'button';
    opener.className = 'cra-invita__abrir';
    const title = document.createElement('span');
    title.className = 'cra-invita__titulo';
    title.textContent = `¡Hola! Soy ${config.name || 'Auri'}, el asistente virtual de Cruz Roja.`;
    const text = document.createElement('span');
    text.className = 'cra-invita__texto';
    text.textContent = INVITE_TEXT;
    opener.append(title, text);
    opener.addEventListener('click', () => {
      hideCard();
      toFrame({ type: WIDGET_MESSAGES.requestOpen });
    });

    const chips = document.createElement('div');
    chips.className = 'cra-invita__chips';
    for (const link of inviteLinks(config.lang)) {
      if ('href' in link) {
        const chip = document.createElement('a');
        chip.className = 'cra-invita__chip';
        chip.href = link.href;
        chip.target = '_blank';
        chip.rel = 'noopener noreferrer';
        chip.textContent = link.label;
        chip.addEventListener('click', hideCard);
        chips.appendChild(chip);
        continue;
      }
      // "Cursos": the chat, opened at the course categories.
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'cra-invita__chip';
      chip.textContent = link.label;
      chip.addEventListener('click', () => {
        hideCard();
        toFrame({ type: WIDGET_MESSAGES.startCourses });
      });
      chips.appendChild(chip);
    }

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'cra-invita__cerrar';
    close.setAttribute('aria-label', 'Cerrar el mensaje del asistente');
    close.textContent = '✕';
    close.addEventListener('click', hideCard);

    aside.append(opener, chips, close);
    // Before the dismiss control, which stays last in the tab order.
    box.insertBefore(aside, dismiss);
    card = aside;
    window.requestAnimationFrame(() => aside.classList.add('cra-invita--visible'));
  };

  // ── pointer forwarding ───────────────────────────────────────

  /**
   * The position goes already normalised (distance from Auri's centre over the
   * window size, clamped to −1..1), not raw coordinates: the iframe has no
   * business knowing where it is placed or how big the page containing it is.
   */
  const sendPointer = (x: number, y: number): void => {
    const c = characterCentre();
    const w = window.innerWidth || 1;
    const h = window.innerHeight || 1;
    toFrame({
      type: WIDGET_MESSAGES.pointer,
      x: Math.max(-1, Math.min(1, (x - c.x) / w)),
      y: Math.max(-1, Math.min(1, (y - c.y) / h)),
      vw: w,
      vh: h,
    });
  };

  let pending: { x: number; y: number } | null = null;
  let queued = 0;
  document.addEventListener(
    'mousemove',
    (event) => {
      if (!ready) return;
      pending = { x: event.clientX, y: event.clientY };
      if (queued) return;
      // One message per frame at most: mousemove fires far above the refresh
      // rate and the iframe cannot paint any faster.
      queued = window.requestAnimationFrame(() => {
        queued = 0;
        if (pending) sendPointer(pending.x, pending.y);
      });
    },
    { passive: true },
  );
  // When the mouse leaves the window she faces front again; otherwise she stays
  // frozen staring at the edge she left by.
  document.documentElement.addEventListener('mouseleave', () => {
    pending = null;
    if (ready) {
      toFrame({
        type: WIDGET_MESSAGES.pointer,
        x: 0,
        y: 0,
        vw: window.innerWidth || 1,
        vh: window.innerHeight || 1,
      });
    }
  });

  // ── controls and messages ──────────────────────────────────────────

  const moveAside = (): void => {
    store.set(KEY_MOVED, '1');
    hideCard();
    flyTo('derecha');
  };

  dismiss.addEventListener('click', moveAside);

  window.addEventListener('resize', () => {
    if (open) layout();
  });

  // ── the frame ──────────────────────────────────────────────────────

  let requested = false;
  let silence = 0;

  /**
   * If the assistant does not answer, nothing has happened. Everything out
   * here is painted by this file; if the frame never arrives, the visitor would
   * see a still that never answers. Its sign of life is `rag:ready`, sent
   * before the 3D scene loads, so waiting for it delays nothing.
   *
   * Hidden rather than removed: removing the box unloads the frame, and a
   * frame that is only slow (tabs restored together at browser start, a cold
   * cache, a weak connection) would then never get to answer. The page is
   * left as it was and `rag:ready`, whenever it comes, shows her.
   */
  const giveUp = (): void => {
    if (ready) return;
    clearSpotlight();
    hideCard();
    hideLoading();
    open = false;
    openWhenReady = false;
    layout();
    box.classList.add('cra--callada');
  };

  /**
   * The silence timer runs only while the tab is visible. A background tab's
   * frame is throttled or not loaded at all, so time spent hidden says nothing
   * about whether the assistant is working.
   */
  const armSilence = (): void => {
    window.clearTimeout(silence);
    if (ready || !requested || document.visibilityState === 'hidden') return;
    silence = window.setTimeout(giveUp, SILENCE_MS);
  };
  document.addEventListener('visibilitychange', armSilence);

  /** Points the iframe at the frame document. Once; the silence timer starts here. */
  const startFrame = (): void => {
    if (requested) return;
    requested = true;

    const frameUrl = new URL(`${config.apiBase}/widget/frame`);
    frameUrl.searchParams.set('key', config.key);
    frameUrl.searchParams.set('accent', config.accent);
    // Where she is now, which a visitor may already have changed.
    frameUrl.searchParams.set('pos', placement);
    if (config.greeting) frameUrl.searchParams.set('greeting', config.greeting);
    if (config.name) frameUrl.searchParams.set('name', config.name);
    if (config.role) frameUrl.searchParams.set('role', config.role);
    if (config.lang) frameUrl.searchParams.set('lang', config.lang);
    // The page the visitor is on, for `Conversation.pageUrl`. Read here rather
    // than inside the frame, where `location` is the API's own URL.
    frameUrl.searchParams.set('page', window.location.href.slice(0, 2000));
    frame.src = frameUrl.toString();

    armSilence();
  };

  // ── the loading panel ──────────────────────────────────────────────

  const showLoading = (): void => {
    if (loading) return;
    const node = document.createElement('div');
    node.className = 'cra-cargando';
    node.setAttribute('role', 'status');
    const spinner = document.createElement('span');
    spinner.className = 'cra-cargando__giro';
    spinner.setAttribute('aria-hidden', 'true');
    const text = document.createElement('span');
    text.textContent = config.lang.startsWith('en') ? 'One moment…' : 'Un momento…';
    node.append(spinner, text);
    box.insertBefore(node, dismiss);
    loading = node;
    window.requestAnimationFrame(() => node.classList.add('cra-cargando--visible'));
  };

  /** Once the real panel is open over it, two frames later so it has been painted. */
  const hideLoading = (): void => {
    const node = loading;
    if (!node) return;
    loading = null;
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => {
        node.classList.remove('cra-cargando--visible');
        window.setTimeout(() => node.remove(), CARD_REMOVE_MS);
      }),
    );
  };

  // A hand on the way to her is a reason to stop waiting for `load`: the
  // press that follows will want the panel.
  (['pointerenter', 'pointerdown', 'focus'] as const).forEach((name) =>
    standIn.addEventListener(name, startFrame, { once: true, passive: true }),
  );

  standIn.addEventListener('click', () => {
    // Pressed during its own fade-out: the frame is there already.
    if (ready) {
      toFrame({ type: WIDGET_MESSAGES.requestOpen });
      return;
    }
    if (openWhenReady) return;
    openWhenReady = true;
    clearSpotlight();
    hideCard();
    startFrame();
    open = true;
    showLoading();
    layout();
  });

  window.addEventListener('message', (event: MessageEvent<unknown>) => {
    // Anything on the page can postMessage, and on WordPress plenty of things
    // do. Both checks matter: the origin proves who sent it, and the source
    // check proves it came from our frame rather than another iframe on the
    // same host. Without them a third-party script could resize this to cover
    // the client's site.
    if (event.origin !== expectedOrigin) return;
    if (event.source !== frame.contentWindow) return;

    const data = event.data as WidgetMessage | null;
    if (!data || typeof data.type !== 'string') return;

    switch (data.type) {
      case WIDGET_MESSAGES.ready: {
        if (ready) break;
        // Held invisible until now, so the character never appears at the
        // wrong size or in the default colour while the frame is booting.
        ready = true;
        window.clearTimeout(silence);
        document.removeEventListener('visibilitychange', armSilence);
        // Late, after the silence hid her: she comes back now.
        box.classList.remove('cra--callada');
        layout();
        // Sent at once, looking straight ahead: until it arrives the frame
        // normalises against its own 320px viewport, and the first turn would
        // shoot off.
        toFrame({
          type: WIDGET_MESSAGES.pointer,
          x: 0,
          y: 0,
          vw: window.innerWidth || 1,
          vh: window.innerHeight || 1,
        });
        // The frame drew her in the commit that sent this, underneath the
        // stand-in; two frames on it has been painted and the stand-in goes.
        box.classList.add('cra--listo');
        window.requestAnimationFrame(() =>
          window.requestAnimationFrame(() => {
            standIn.classList.add('cra-espera--fuera');
            window.setTimeout(() => standIn.remove(), STAND_IN_REMOVE_MS);
          }),
        );
        if (openWhenReady) {
          // Pressed while it loaded: straight to the panel, which brings its
          // own privacy notice and greeting, in their own order.
          toFrame({ type: WIDGET_MESSAGES.requestOpen });
          break;
        }
        // The arrival's last two beats wait for the frame: the invitation
        // opens it, and nothing should invite before it can answer.
        window.requestAnimationFrame(() => {
          const ran = spotlightOnce();
          window.setTimeout(showCard, ran ? CARD_DELAY_AFTER_SPOTLIGHT_MS : CARD_DELAY_MS);
        });
        break;
      }

      case WIDGET_MESSAGES.open:
        open = true;
        openWhenReady = false;
        clearSpotlight();
        hideCard();
        layout();
        hideLoading();
        break;

      case WIDGET_MESSAGES.close:
        open = false;
        layout();
        break;

      case WIDGET_MESSAGES.recenter:
        // Expand, or a double-click in a corner: back to the middle, the way the visit started.
        store.remove(KEY_MOVED);
        flyTo('centro');
        break;

      case WIDGET_MESSAGES.dock:
        // Collapse from the centred header: the same move as the dismiss control.
        moveAside();
        break;
    }
  });

  layout();
  document.body.append(veil, spotlight, box);
  // The stand-in is the launcher from here; the frame loads behind it.
  window.requestAnimationFrame(() => box.classList.add('cra--visible'));

  // Not before the page's `load`, unless the visitor reaches for her: an
  // iframe that starts loading earlier holds the host's own `load` event back
  // until it has finished, and the client's site would measure slower.
  if (document.readyState === 'complete') {
    startFrame();
  } else {
    window.addEventListener('load', startFrame, { once: true });
    window.setTimeout(startFrame, FRAME_BACKSTOP_MS);
  }
}

const config = readConfig();
if (config) {
  // `document.currentScript` has already been read, so waiting is safe.
  //
  // Not for the page's `load`: the stand-in is one inlined image and three
  // fixed elements, which cost the page nothing to paint, and `load` waits for
  // every image on it. Only for `<body>`, which an async tag in `<head>` can
  // run before: a cached loader usually does. The parser opens `<body>` long
  // before `DOMContentLoaded`, so its arrival is watched for (one child list,
  // not the subtree), and `DOMContentLoaded` is the fallback.
  let booted = false;
  const mount = (): void => {
    if (booted || !document.body) return;
    booted = true;
    boot(config);
  };
  if (document.body) {
    mount();
  } else {
    const watch = new MutationObserver(() => {
      if (!document.body) return;
      watch.disconnect();
      mount();
    });
    watch.observe(document.documentElement, { childList: true });
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        watch.disconnect();
        mount();
      },
      { once: true },
    );
  }
}
